import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { securityError, securityRequest } from '../../services/accountSecurityService';
import SecurityStyles from '../../components/accountSecurity/SecurityStyles';
import PasswordField from '../../components/accountSecurity/PasswordField';
import DarkModeToggle from '../../components/common/DarkModeToggle';
import BrandStripes from '../../components/common/BrandStripes';
export function SecurityFrame({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const {
    isDark
  } = useTheme();
  return <div className="account-security security-layout" data-dark={isDark}><SecurityStyles />
        <div style={{
      position: 'fixed',
      right: 20,
      top: 20,
      zIndex: 20
    }}><DarkModeToggle /></div>
        <aside className="security-brand"><Link to="/"><img src={`${process.env.PUBLIC_URL}/logo-comarca-blanco.png?v=2`} alt="Co Marca" /></Link><h1>Tu cuenta,<br />bajo tu control.</h1><p>Protege tu acceso con ayuda de Co Marca.</p><div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%'
      }}><BrandStripes /></div></aside>
        <main className="security-main"><div className="security-card"><h2>{title}</h2>{children}<p>{isAuthenticated ? <button type="button" className="security-button secondary" onClick={() => {
          if (window.history.state?.idx > 0) navigate(-1);
          else navigate('/');
        }}>Volver atrás</button> : <Link to="/login">Volver al inicio de sesión</Link>}</p></div></main></div>;
}
export default function CompromisedAccountPage({
  alert = false
}: {
  alert?: boolean;
}) {
  // These routes share a component type; reset capabilities and form state
  // when navigating between an activity alert and assisted recovery.
  return <CompromisedAccountFlow key={alert ? 'alert' : 'recovery'} alert={alert} />;
}
function CompromisedAccountFlow({ alert }: { alert: boolean }) {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
  const [stage, setStage] = useState(token ? alert ? 'alert' : 'reset' : 'start');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activity, setActivity] = useState<{
    email: string;
    ip: string;
    device: string;
    created_at: string;
  } | null>(null);
  useEffect(() => {
    // Keep email capabilities out of URLs, storage and API access logs.
    if (token) window.history.replaceState(null, '', window.location.pathname);
    if (alert && token) securityRequest<typeof activity>('alerts/action', {
      token
    }).then(setActivity).catch(e => setError(securityError(e)));
    if (alert && !token) setError('Abre el enlace recibido por correo.');
    if (!alert && !token) securityRequest<{
      status: string;
    }>('assisted/status', undefined, 'get').then(result => {
      if (result.status === 'contact') setStage('code');
      if (['review', 'approved', 'rejected'].includes(result.status)) setStage('review');
    }).catch(() => {/* A new browser has no recovery case yet. */});
  }, [alert, token]);
  async function run(path: string, data?: unknown, method: 'get' | 'post' = 'post') {
    setBusy(true);
    setError('');
    try {
      const result = await securityRequest<{
        detail?: string;
        status?: string;
        delivery_failed?: boolean;
      }>(path, data, method);
      const states: Record<string, string> = {
        review: 'Pendiente de revisión de identidad',
        approved: 'Autorizada; revisa tu correo',
        rejected: 'No autorizada; contacta a Co Marca',
        completed: 'Completada',
        contact: 'Verifica tu contacto'
      };
      setMessage(result.detail || `${states[result.status || ''] || 'Solicitud pendiente'}.${result.delivery_failed ? ' No se pudo confirmar una entrega de correo; contacta a Co Marca.' : ''}`);
      return true;
    } catch (e) {
      setError(securityError(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (stage === 'start' && (await run('assisted/start', {
      email,
      contact_email: contact,
      description
    }))) setStage('code');else if (stage === 'code' && (await run('assisted/contact', {
      code
    }))) {
      setCode('');
      setStage('review');
    } else if (stage === 'reset' && (await run('assisted/complete', {
      token,
      new_password: password,
      confirm_password: confirm
    }))) {
      setPassword('');
      setConfirm('');
      setStage('done');
    }
  }
  return <SecurityFrame title={alert ? '¿Fuiste tú?' : 'Recuperación asistida'}>
        <p>{alert ? 'Registramos varios intentos fallidos. Confirmar esta actividad no inicia sesión ni elimina bloqueos.' : 'Si perdiste tus métodos de acceso o tu cuenta fue comprometida, verificaremos un correo seguro y Co Marca revisará tu identidad antes de autorizar cambios. No envíes contraseñas, códigos ni documentos aquí.'}</p>
        {error && <div className="security-feedback error" role="alert">{error}</div>}{message && <div className="security-feedback success" role="status">{message}</div>}
        {!alert && ['start', 'code', 'reset'].includes(stage) && <form className="security-form" onSubmit={submit}>
            {stage === 'start' && <><label className="security-field">Correo de la cuenta<input type="email" required maxLength={254} value={email} onChange={e => setEmail(e.target.value)} /></label><label className="security-field">Correo seguro de contacto<input type="email" required maxLength={254} value={contact} onChange={e => setContact(e.target.value)} /></label><label className="security-field">Describe lo ocurrido<input required minLength={20} maxLength={1000} value={description} onChange={e => setDescription(e.target.value)} /></label></>}
            {stage === 'code' && <label className="security-field">Código del correo de contacto<input required inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={code} onChange={e => setCode(e.target.value)} /></label>}
            {stage === 'reset' && <><p>El enlace dura 15 minutos y es de un solo uso. La verificación en dos pasos quedará activa por el correo de contacto revisado.</p><PasswordField label="Nueva contraseña" required minLength={8} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} /><PasswordField label="Confirmar contraseña" required minLength={8} autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} /></>}
            <button className="security-button" disabled={busy}>{busy ? 'Procesando…' : stage === 'start' ? 'Solicitar revisión' : stage === 'code' ? 'Verificar contacto' : 'Restablecer acceso'}</button></form>}
        {stage === 'code' && <button className="security-button secondary" disabled={busy} onClick={() => run('assisted/resend', {})}>Reenviar código de contacto</button>}
        {!alert && ['code', 'review'].includes(stage) && <button className="security-button secondary" disabled={busy} onClick={() => {
      setStage('start');
      setMessage('');
      setCode('');
    }}>Iniciar otra solicitud</button>}
        {stage === 'review' && <button className="security-button secondary" disabled={busy} onClick={() => run('assisted/status', undefined, 'get')}>Consultar estado</button>}
        {alert && activity && stage !== 'done' && <><p>Cuenta: {activity.email}<br />IP: {activity.ip}<br />Fecha: {new Date(activity.created_at).toLocaleString('es-BO')}<br />Navegador: {activity.device || 'No informado'}</p><button className="security-button" disabled={busy} onClick={async () => {
        if (await run('alerts/action', {
          token,
          decision: 'me'
        })) setStage('done');
      }}>Fui yo</button>{' '}<button className="security-button secondary" disabled={busy} onClick={async () => {
        if (await run('alerts/action', {
          token,
          decision: 'not_me'
        })) setStage('done');
      }}>No fui yo: cerrar sesiones</button></>}
        {(alert || stage === 'review') && <p><Link to="/recuperar-contrasena">Recuperar contraseña</Link><br /><Link to="/recuperar-cuenta-comprometida">Solicitar recuperación asistida</Link></p>}
    </SecurityFrame>;
}
